import React, { useMemo, useState } from 'react';
import {
  Grid,
  Card,
  Typography,
  CardMedia,
  Stack,
  Link,
  Box,
} from '@mui/material';
import { getEntityImageUrl } from 'api/utils';
import noAvatar from 'assets/images/no-avatar.png';
import * as Icons from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getTheme } from 'config/theme';
import WeekGrid from './weekGrid';
import CommunityPricingBubble from './communityPricingBubble';

const getIconComponent = (iconName) => {
  return Icons[iconName] || Icons.Help;
};

const CARD_HEIGHT = 160; // Changed from 200
const IMAGE_SECTION_HEIGHT = 90; // Changed from 120
const WEEK_GRID_HEIGHT = 70; // Kept the same


const MobileCardItem = ({
  space,
  setSelectedSpace,
  startDate,
  endDate,
  onDetailClick,
  onMouseEnter,
  onMouseLeave,
  isHovered,
  isSelected
}) => {
  const [theme] = useState(getTheme(null));

  const dateRangeWithPrices = useMemo(() => {
    if (!startDate || !endDate || !space.item_price) return [];

    const dates = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);
    let priceIndex = 0;

    while (currentDate <= end) {
      dates.push({
        date: new Date(currentDate),
        price: space.item_price[priceIndex] || '-',
        original_price : space?.original_price[priceIndex] || '-',
        dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' })
      });
      currentDate.setDate(currentDate.getDate() + 1);
      priceIndex = Math.min(priceIndex + 1, space.item_price.length - 1);
    }

    return dates;
  }, [startDate, endDate, space.item_price]);

    const getPriceColor = useMemo(() => {
      if (dateRangeWithPrices.length === 0) return () => 'blue.sad';
    
      // Return a function that compares price and original_price for each day
      return (price, originalPrice, minimum_price) => {

        // If there's no original price or price equals original price, return blue.sad
        if (!originalPrice || price === originalPrice) return 'blue.sad';

        // If it's the lowest price of the week, return primary.main
        if (price === minimum_price) return 'primary.main';
                
        // If price is lower than original price (discount), return green.sad
        if (price < originalPrice) return 'green.sad';
        
        // Default fallback
        return 'blue.sad';
      };
    }, [dateRangeWithPrices]);

  const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(space.venue_address)}`;

  return (
    <Grid item xs={12}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: CARD_HEIGHT,
          boxShadow: 2,
          transition: 'transform 0.2s',
          transform: isHovered || isSelected ? 'scale(1.02)' : 'none',
          border: isSelected ? '2px solid #1976d2' : 'none',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: 3
          }
        }}
        onMouseEnter={() => onMouseEnter && onMouseEnter(space.slug)}
        onMouseLeave={() => onMouseLeave && onMouseLeave(space.slug)}
      >
        {/* First Row: Image and Details */}
        <Box sx={{
          display: 'flex',
          p: 0.75,
          height: IMAGE_SECTION_HEIGHT,
          minHeight: IMAGE_SECTION_HEIGHT
        }}>
          {/* Image Section */}
          <Box
            sx={{
              width: IMAGE_SECTION_HEIGHT - 8, // Changed from 25%, now using fixed size
              height: IMAGE_SECTION_HEIGHT - 8,
              flexShrink: 0, // Added to prevent squishing
              position: 'relative' // Keep this
            }}
          >
            <CommunityPricingBubble community={space.applied_community} compact />
            <CardMedia
              component="img"
              image={getEntityImageUrl(space, noAvatar)}
              alt={space.venue_name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 1
              }}
            />
          </Box>

          {/* Info Section */}
          <Stack
            spacing={0.75} // Changed from 0.5
            sx={{
              flex: 1,
              pl: 2,
              py: 0.5, // Added vertical padding
              overflow: 'hidden'
            }}
          >
            {/* Venue Name */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.section_title.main,
                fontSize: '1rem',
                lineHeight: 1.2, // Added this
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              <Link
                onClick={() => setSelectedSpace(space)}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {space.venue_name}
              </Link>
            </Typography>

            {/* Address */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                lineHeight: 1.2, // Added this
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              <Link
                href={googleMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {space.venue_address}
                <OpenInNewIcon sx={{ ml: 0.5, fontSize: '0.875rem' }} />
              </Link>
            </Typography>

            {/* Amenities */}
            <Box sx={{
              display: 'flex',
              gap: 1,
              height: '20px', // Added fixed height
              alignItems: 'center'
            }}>
              {space?.venue_services?.map((amenity) => {
                const IconComponent = getIconComponent(amenity.icon);
                return (
                  <Box
                    key={amenity.slug}
                    title={amenity.description}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <IconComponent sx={{ fontSize: '1rem', color: theme.icons_lite.main }} />
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </Box>


        {/* Second Row: Price Calendar */}
        <Box sx={{
          height: WEEK_GRID_HEIGHT,
          display: 'flex',
          alignItems: 'center'
        }}>
          <WeekGrid
            dateRangeWithPrices={dateRangeWithPrices}
            getPriceColor={getPriceColor}
            onDayClick={(space, date) => onDetailClick(space, date)}
            space={space}
            variant="compact"
            maxWidth="98%"
          />
        </Box>
      </Card>
    </Grid>
  );
};

export default MobileCardItem;